// Interpret tee times without a zone as CET/CEST (Europe/Berlin), not UTC.
export function formatLocalShortTimeFromCET(iso: string): string {
    try {
        if (!iso) return '-';

        let s = iso.trim();
        if (!s.includes('T')) s = s.replace(' ', 'T');

        const hasZone = /[zZ]$|[+\-]\d{2}:\d{2}$/.test(s);

        let d: Date;

        if (hasZone) {
            d = new Date(s);
        } else {
            // No zone provided → treat as CET/CEST (Europe/Berlin).
            // We'll decide between +01:00 (CET) and +02:00 (CEST) for that calendar date.

            // Parse hour/minute from the naive local time string to validate the mapping.
            const m = s.match(/T(\d{2}):(\d{2})/);
            const origHour = m ? parseInt(m[1], 10) : 0;
            const origMin  = m ? parseInt(m[2], 10) : 0;

            const sCET  = s + '+01:00'; // CET candidate
            const sCEST = s + '+02:00'; // CEST candidate

            const dCET  = new Date(sCET);
            const dCEST = new Date(sCEST);

            // Formatter that tells us what the *Europe/Berlin* local clock is for a given Date
            const berlinFmt = new Intl.DateTimeFormat('en-US', {
                hour: 'numeric',
                minute: '2-digit',
                hour12: false,
                timeZone: 'Europe/Berlin'
            });

            const getHM = (date: Date) => {
                const parts = berlinFmt.formatToParts(date);
                const h = Number(parts.find(p => p.type === 'hour')?.value ?? '0');
                const mi = Number(parts.find(p => p.type === 'minute')?.value ?? '0');
                return { h, mi };
            };

            const hmCET  = getHM(dCET);
            const hmCEST = getHM(dCEST);

            // Pick the offset whose Berlin-local clock matches the time we received.
            if (hmCET.h === origHour && hmCET.mi === origMin) {
                d = dCET;
            } else if (hmCEST.h === origHour && hmCEST.mi === origMin) {
                d = dCEST;
            } else {
                // Fallback: assume CET (safer in winter; also avoids edge-case ambiguity around DST shifts)
                d = dCET;
            }
        }

        // Display in the user's current timezone/locale as a short time.
        const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
        return new Intl.DateTimeFormat(navigator.language, {
            hour: 'numeric',
            minute: '2-digit',
            timeZone: tz,
        }).format(d);

    } catch {
        return '-';
    }
}


export function formatLocalShortTimeFromUTC(iso: string): string {
    try {
        if (!iso) return '-';

        let s = iso.trim();

        if (!s.includes('T')) s = s.replace(' ', 'T');

        const hasZone = /[zZ]$|[+\-]\d{2}:\d{2}$/.test(s);

        // if no timezone info, treat as UTC
        if (!hasZone) s += 'Z';

        const d = new Date(s);

        const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;

        return new Intl.DateTimeFormat(navigator.language, {
            hour: 'numeric',
            minute: '2-digit',
            timeZone: tz,
        }).format(d);
    } catch {
        return '-';
    }
}